import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogAPI } from '../services/api';
import { 
  FiArrowLeft, 
  FiClock, 
  FiCalendar, 
  FiUser, 
  FiTag, 
  FiShare2, 
  FiFacebook, 
  FiLinkedin, 
  FiTwitter,
  FiEye
} from 'react-icons/fi';

const BlogDetail = () => {
  const { slug } = useParams();

  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      window.scrollTo(0, 0);
      fetchBlog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const response = await blogAPI.getBySlug(slug);
      const currentBlog = response.data?.data || response.data;
      setBlog(currentBlog);

      if (currentBlog?.category) {
        const relatedResponse = await blogAPI.getAll({
          category: currentBlog.category,
          sortBy: 'date',
          sortOrder: 'desc',
          page: 1,
          limit: 4 // Fetch 4, filter out current
        });

        const list =
          relatedResponse.data?.data?.blogs ||
          relatedResponse.data?.blogs ||
          relatedResponse.data ||
          [];

        setRelatedBlogs(
          (Array.isArray(list) ? list : [])
            .filter((b) => b.slug !== currentBlog.slug)
            .slice(0, 3)
        );
      } else {
        setRelatedBlogs([]);
      }
    } catch (error) {
      console.error("Error fetching blog:", error);
      setBlog(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (content) => {
    if (!content) return 1;
    const words = content.split(' ').length;
    return Math.ceil(words / 200);
  };

  // --- Loading Skeleton ---
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            {/* Header Skeleton */}
            <div className="space-y-4 max-w-3xl">
              <div className="h-4 bg-slate-200 rounded w-32"></div>
              <div className="h-10 md:h-14 bg-slate-200 rounded w-3/4"></div>
              <div className="flex gap-4">
                <div className="h-4 bg-slate-200 rounded w-24"></div>
                <div className="h-4 bg-slate-200 rounded w-24"></div>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-[1fr_350px] gap-8">
              {/* Main Content Skeleton */}
              <div className="space-y-6">
                <div className="w-full aspect-video bg-slate-200 rounded-2xl"></div>
                <div className="space-y-4">
                  <div className="h-4 bg-slate-200 rounded w-full"></div>
                  <div className="h-4 bg-slate-200 rounded w-full"></div>
                  <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                  <div className="h-4 bg-slate-200 rounded w-full"></div>
                </div>
              </div>
              
              {/* Sidebar Skeleton */}
              <div className="space-y-6">
                <div className="h-64 bg-slate-200 rounded-2xl"></div>
                <div className="h-96 bg-slate-200 rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Not Found State ---
  if (!blog) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
            😕
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Article not found</h1>
          <p className="text-slate-600 mb-8">
            The article you are looking for might have been removed or is temporarily unavailable.
          </p>
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
          >
            <FiArrowLeft /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const imageUrl = blog.featuredImage?.startsWith('http')
    ? blog.featuredImage
    : `${process.env.REACT_APP_API_URL || ''}${blog.featuredImage}`;

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-20">
      
      {/* Navigation / Breadcrumb */}
      <div className="sticky top-0 z-30 bg-slate-50/80 backdrop-blur-xl border-b border-slate-200 mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <nav className="flex items-center gap-2 text-sm text-slate-500 overflow-hidden whitespace-nowrap">
              <Link to="/" className="hover:text-indigo-600 transition-colors">Home</Link>
              <span>/</span>
              <Link to="/blogs" className="hover:text-indigo-600 transition-colors">Blog</Link>
              <span>/</span>
              <span className="text-slate-800 font-medium truncate max-w-[150px] sm:max-w-xs">
                {blog.title}
              </span>
            </nav>
            <Link 
              to="/blogs" 
              className="hidden sm:flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
            >
              <FiArrowLeft /> Back to List
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_350px] gap-8 xl:gap-12">
          
          {/* --- Main Content Column --- */}
          <motion.article 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="min-w-0" // Prevents overflow issues
          >
            {/* Header Info */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold uppercase tracking-wide">
                  {blog.category || 'Article'}
                </span>
                <span className="text-slate-400 text-xs font-medium flex items-center gap-1">
                  <FiClock className="w-3 h-3" /> {getReadingTime(blog.content)} min read
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6">
                {blog.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500 border-b border-slate-200 pb-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                    {blog.Author?.firstName?.charAt(0) || 'A'}
                  </div>
                  <span className="font-medium text-slate-700">
                    {blog.Author ? `${blog.Author.firstName} ${blog.Author.lastName}` : 'Admin'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FiCalendar className="w-4 h-4" />
                  {formatDate(blog.publishedAt || blog.createdAt)}
                </div>
                <div className="flex items-center gap-1.5">
                  <FiEye className="w-4 h-4" />
                  {blog.views || 0} views
                </div>
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative aspect-video rounded-2xl overflow-hidden mb-10 shadow-lg border border-slate-100">
              <img
                src={imageUrl}
                alt={blog.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/800x450?text=No+Image'; }}
              />
            </div>

            {/* Content Body */}
            <div className="prose prose-lg prose-slate max-w-none prose-headings:font-bold prose-a:text-indigo-600 hover:prose-a:text-indigo-500 prose-img:rounded-xl">
               {/* Using dangerous HTML but assuming API content is sanitized */}
              <div dangerouslySetInnerHTML={{ __html: blog.content }} />
            </div>

            {/* Footer Tags & Share */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                
                {/* Tags */}
                {blog.tags && blog.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.map((tag, i) => (
                      <span key={i} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 transition-colors cursor-pointer">
                        <FiTag className="w-3 h-3" /> {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Share Buttons */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <FiShare2 /> Share:
                  </span>
                  <button className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                    <FiFacebook className="w-5 h-5" />
                  </button>
                  <button className="p-2 rounded-full bg-sky-50 text-sky-500 hover:bg-sky-100 transition-colors">
                    <FiTwitter className="w-5 h-5" />
                  </button>
                  <button className="p-2 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors">
                    <FiLinkedin className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.article>

          {/* --- Sidebar Column --- */}
          <aside className="space-y-8">
            <div className="sticky top-24 space-y-8">
              
              {/* Author Card */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold ring-4 ring-slate-50">
                    {blog.Author?.firstName?.charAt(0) || <FiUser />}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">
                      {blog.Author?.firstName} {blog.Author?.lastName}
                    </h3>
                    <p className="text-sm text-slate-500">Real Estate Expert</p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  Providing insights into market trends, investment strategies, and modern living solutions.
                </p>
                <button className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 hover:text-indigo-600 transition-all text-sm">
                  View Profile
                </button>
              </motion.div>

              {/* Related Articles */}
              {relatedBlogs.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
                >
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
                    Related Articles
                  </h3>
                  <div className="space-y-6">
                    {relatedBlogs.map((item) => (
                      <Link 
                        key={item.id} 
                        to={`/blog/${item.slug}`}
                        className="group flex gap-4 items-start"
                      >
                        <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-slate-100">
                          <img 
                            src={item.featuredImage || '/api/placeholder/100/100'} 
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/100'}
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800 text-sm leading-snug mb-1 group-hover:text-indigo-600 transition-colors line-clamp-2">
                            {item.title}
                          </h4>
                          <span className="text-xs text-slate-400">
                            {formatDate(item.publishedAt)}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
};

export default BlogDetail;